import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  
  // Staff session management
  const [activeStaff, setActiveStaff] = useState(null);
  const [staffToken, setStaffToken] = useState(localStorage.getItem('staffToken'));
  const [showStaffLogin, setShowStaffLogin] = useState(false);

  // Keep the business name where non-React modules (label printing, PDFs) can read it
  useEffect(() => {
    if (user?.business_name) localStorage.setItem('business_name', user.business_name);
  }, [user?.business_name]);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedStaffToken = localStorage.getItem('staffToken');
      const storedStaff = localStorage.getItem('activeStaff');
      
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
          
          // Restore staff session if exists
          if (storedStaffToken && storedStaff) {
            try {
              const staffData = JSON.parse(storedStaff);
              setActiveStaff(staffData);
              setStaffToken(storedStaffToken);
            } catch {
              // Invalid staff session, clear it
              localStorage.removeItem('staffToken');
              localStorage.removeItem('activeStaff');
            }
          }
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('staffToken');
          localStorage.removeItem('activeStaff');
          setToken(null);
          setUser(null);
          setActiveStaff(null);
          setStaffToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Store login (owner/admin) with retry logic for cold start
  const login = async (email, password) => {
    const maxRetries = 3;
    let lastError;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await axios.post(`${API_URL}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        setToken(access_token);
        setUser(userData);
        // Clear any previous staff session
        setActiveStaff(null);
        setStaffToken(null);
        localStorage.removeItem('staffToken');
        localStorage.removeItem('activeStaff');
        return response.data;
      } catch (error) {
        lastError = error;
        // Only retry on network/server errors, not auth failures
        if (error.response?.status === 401) {
          throw error; // Invalid credentials - don't retry
        }
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
        }
      }
    }
    throw lastError;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return response.data;
  };

  // Staff login with initials + PIN
  const staffLogin = async (initials, pin) => {
    if (!token) {
      throw new Error('Store must be logged in first');
    }
    
    const response = await axios.post(`${API_URL}/auth/staff-login`, {
      initials,
      pin,
      store_token: token
    });
    
    const { access_token, staff } = response.data;
    localStorage.setItem('staffToken', access_token);
    localStorage.setItem('activeStaff', JSON.stringify(staff));
    setStaffToken(access_token);
    setActiveStaff(staff);
    setShowStaffLogin(false);
    return response.data;
  };

  // Staff logout (switch user)
  const staffLogout = () => {
    localStorage.removeItem('staffToken');
    localStorage.removeItem('activeStaff');
    setStaffToken(null);
    setActiveStaff(null);
    setShowStaffLogin(true);
  };

  // Full logout (store + staff)
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('staffToken');
    localStorage.removeItem('activeStaff');
    setToken(null);
    setUser(null);
    setStaffToken(null);
    setActiveStaff(null);
    setShowStaffLogin(false);
  };

  // Global 401 handler: auto-logout + redirect to login on expired/invalid token.
  // Without this, every API call after expiry silently fails with "Token expired"
  // and users think buttons are broken.
  const sessionExpiredShown = useRef(false);
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail || '';
        if (status === 401 && /token expired|invalid token/i.test(detail)) {
          // Only on URLs talking to our backend
          const url = error?.config?.url || '';
          if (url.includes(API_URL) || url.startsWith('/api')) {
            if (!sessionExpiredShown.current) {
              sessionExpiredShown.current = true;
              toast.error('Session expired — please sign in again');
              setTimeout(() => { sessionExpiredShown.current = false; }, 5000);
            }
            logout();
            // Hard redirect so all in-flight pages reset cleanly
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const getAuthHeader = () => {
    // Use staff token if available, otherwise use store token
    const activeToken = staffToken || token;
    return activeToken ? { Authorization: `Bearer ${activeToken}` } : {};
  };

  // Get the store token specifically (for staff login)
  const getStoreToken = () => token;

  // Check if staff has specific permission based on role
  const hasPermission = (requiredRole) => {
    const roleHierarchy = { admin: 3, manager: 2, staff: 1 };
    const staffRole = activeStaff?.role || 'admin'; // Default to admin for store owner
    return roleHierarchy[staffRole] >= roleHierarchy[requiredRole];
  };

  // Get current operator name (for receipts, etc.)
  const getCurrentOperator = () => {
    if (activeStaff) {
      return activeStaff.name;
    }
    return user?.name || 'Owner';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      getAuthHeader,
      // Staff-specific
      activeStaff,
      setActiveStaff,
      staffToken,
      setStaffToken,
      staffLogin,
      staffLogout,
      showStaffLogin,
      setShowStaffLogin,
      getStoreToken,
      hasPermission,
      getCurrentOperator
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
